// Mixed-width operations extend the shorter bitset, orInto is the only one that throws if the source is wider.
export type Bitset = Uint32Array

export function createBitset(size: number): Bitset {
  // Clear and fast allocation using unsigned right shift
  return new Uint32Array((size + 31) >>> 5)
}

export function setBit(bits: Bitset, index: number): void {
  bits[index >>> 5]! |= 1 << (index & 31)
}

export function popcount32(word: number): number {
  let v = word | 0
  v = v - ((v >>> 1) & 0x55555555)
  v = (v & 0x33333333) + ((v >>> 2) & 0x33333333)
  v = (v + (v >>> 4)) & 0x0F0F0F0F
  return (Math.imul(v, 0x01010101) >>> 24) & 0xFF
}

export function popcount(bits: Bitset): number {
  let total = 0
  const len = bits.length
  // Localized loop length avoids reading array properties inside the block.
  for (let i = 0; i < len; i++) {
    total += popcount32(bits[i]!)
  }
  return total
}

export function countAnd(a: Bitset, b: Bitset): number {
  let total = 0
  const minLength = a.length < b.length ? a.length : b.length

  for (let i = 0; i < minLength; i++) {
    total += popcount32(a[i]! & b[i]!)
  }
  return total
}

export function orInto(target: Bitset, source: Bitset): void {
  // `Uint32Array` cannot grow, so a wider source would silently undercount instead.
  if (source.length > target.length) {
    throw new RangeError(`orInto: source (${source.length} words) is wider than target (${target.length} words)`)
  }

  for (let i = 0; i < source.length; i++) {
    target[i]! |= source[i]!
  }
}

export function andInto(target: Bitset, source: Bitset): void {
  const minLength = target.length < source.length ? target.length : source.length

  for (let i = 0; i < minLength; i++) {
    target[i]! &= source[i]!
  }

  // If target is larger than source, we need to clear the remaining bits in target
  if (target.length > source.length) {
    target.fill(0, source.length, target.length)
  }
}
